<script lang="ts">
	import Button from '$lib/components/atoms/button/Button.svelte';
	import Icon from '$lib/components/icons/Icon.svelte';
	import Calendar from '$lib/components/molecules/date/rangeCalendar/RangeCalendar.svelte';
	import { MAX_SELECTION_DAYS } from '$lib/components/pois/constants';
	import { DEFAULT_ACCOMMODATION_AVAILABILITY_END_DATES_DAYS_DIFF, DEFAULT_ACCOMMODATION_AVAILABILITY_START_DATES_DAYS_DIFF } from '$lib/constants/searchParams';
	import { getShortDateTimeFormatter } from '$lib/utils/date';
	import * as m from '$paraglide/messages';
	import { languageTag } from '$paraglide/runtime';
	import { CalendarDate, type DateValue, getLocalTimeZone, startOfMonth, today } from '@internationalized/date';
	import { createDateRangePicker, type DateRange } from '@melt-ui/svelte';
	import { watchOnce } from 'runed';
	import { fade } from 'svelte/transition';
	import { DEFAULT_NUMBER_OF_DAY_TO_ADD, displayNumberOfNights } from './utils';

	interface Props {
		initialValues?: DateRange;
		activeDay?: CalendarDate;
		maxSelectionDays?: number;
		withNumberOfNights?: boolean;
		shouldStayOpenAfterClearing?: boolean;
		onClearDates?: () => void;
		onOpen?: (inputId: string) => void;
		onSelectDates?: (datesRange: CalendarDate[]) => void;
	}

	const {
		initialValues = { start: undefined, end: undefined },
		activeDay,
		maxSelectionDays = MAX_SELECTION_DAYS,
		withNumberOfNights = false,
		shouldStayOpenAfterClearing = false,
		onClearDates,
		onOpen,
		onSelectDates,
	}: Props = $props();

	let datesRange: DateRange = $state(initialValues);
	let childRef: Calendar | null = $state(null);
	const todayDate = today(getLocalTimeZone());
	let hasUpdated = $state(false);

	const {
		elements: {
			field,
			trigger,
			content,
			overlay,
		},
		states: { open, value },
		options: { locale },
	} = createDateRangePicker({
		forceVisible: true,
		positioning: { placement: 'bottom-start' },
		closeOnOutsideClick: true,
		defaultValue: initialValues,
		locale: languageTag(),
		onOutsideClick: () => {
			const { start, end } = datesRange;

			if (!start && !end) {
				$value = ({
					start: todayDate.add({ days: DEFAULT_ACCOMMODATION_AVAILABILITY_START_DATES_DAYS_DIFF }),
					end: todayDate.add({ days: DEFAULT_ACCOMMODATION_AVAILABILITY_END_DATES_DAYS_DIFF }),
				});
			}

			if (start && !end) {
				$value = ({ start, end: start.add({ days: DEFAULT_NUMBER_OF_DAY_TO_ADD }) });
			}

			onSelectDates?.([$value.start as CalendarDate, $value.end as CalendarDate]);
		},
		onOpenChange: (isOpen) => {
			if (isOpen.next) {
				onOpen?.('date-range-picker');
			}

			return isOpen.next;
		},
	});

	// As we are using Svelte 5, the $value store from melt ui is not synchronized with the initialValues
	// so we need to use a custom effect to synchronize the value once
	// this should be removed when we upgrade melt-ui to Svelte 5
	watchOnce(() => initialValues, (initialValues) => {
		if (initialValues && $value.start !== initialValues.start && $value.end !== initialValues.end && !$open && !hasUpdated) {
			$value = initialValues;
			hasUpdated = true;
		}
	});

	function clear() {
		$value = ({ start: undefined, end: undefined });

		if (shouldStayOpenAfterClearing) {
			$open = true;
		}
	}

	function handleClearInput(e: MouseEvent) {
		e.preventDefault();

		clear();
		if (childRef) {
			childRef.clearState();
		}

		onClearDates?.();
	}

	function handleCancelation() {
		clear();
		onClearDates?.();
	}

	function handleValidation(e: CustomEvent<{ selectedDates: DateRange | undefined }>) {
		const dates = e.detail.selectedDates;
		if (!dates) {
			return;
		}

		if (dates?.start && !dates?.end) {
			$value = { start: dates.start, end: dates.start.add({ days: DEFAULT_NUMBER_OF_DAY_TO_ADD }) };
		}

		if (dates?.start && dates?.end) {
			$value = dates;
		}

		onSelectDates?.([$value.start as CalendarDate, $value.end as CalendarDate]);
		$open = false;
	}

	function dateDisabled(date: CalendarDate) {
		return date.compare(todayDate) < 0 || !!(datesRange?.start && date.compare(datesRange.start.add({ days: maxSelectionDays })) > 0);
	}

	function isDateDisabled(dateValue: DateValue) {
		const date = dateValue as CalendarDate;
		return dateDisabled(date);
	}

	function formatDate(date: DateValue) {
		return getShortDateTimeFormatter(languageTag()).format(date.toDate(getLocalTimeZone()));
	}
</script>

<div {...$field} class="flex w-full bg-transparent max-w-full" data-testid="hotel-date-range-picker">
	<div class="w-full h-44px b-1 border-rd-2 border-solid border-coloredneutral-weakest bg-neutral-weakest inline-flex items-center justify-between">
		<!-- eslint-disable-next-line svelte/require-store-reactive-access -->
		<button use:trigger {...$trigger} class="{$value.end ? 'p-l-0' : 'p-r-2'} p-l-2 py-2 btn-ghost-primary bg-transparent border-0 inline-flex items-center justify-start w-full font-400 text-4 gap-1 truncate" aria-label={m.date_range_picker__trigger_aria_label()}>
			<Icon tag="span" icon="calendar" class="text-coloredneutral" ariaHidden="true" />
			{#key $locale}
				<span class="gap-0">
					{#if $value.start}
						<span class="text-coloredneutral text-4">{formatDate($value.start)}</span>
					{:else}
						<span class="text-coloredneutral-weak"> {m.date_range_picker__arrival_placeholder()}</span>
					{/if}
				</span>
			{/key}
			<span aria-hidden="true" class={$value.end && $value.start ? 'text-coloredneutral' : 'text-coloredneutral-weak'}>&#10141;</span>
			{#key $locale}
				{#if $value.end}
					<span class="text-coloredneutral text-4">{formatDate($value.end)}</span>
				{:else}
					<span class="text-coloredneutral-weak">{m.date_range_picker__departure_placeholder()}</span>
				{/if}
			{/key}
			{#if $value.start && $value.end && withNumberOfNights}
				<span class="text-coloredneutral-soft text-3.5">({displayNumberOfNights($value.start, $value.end)})</span>
			{/if}
		</button>
		{#if $value.end}
			<Button
				variant="ghost"
				icon="close"
				ariaLabel={m.date_range_picker__close_aria_label()}
				disabled={false}
				class="p-0 p-r-2 text-6"
				onclick={handleClearInput}
				onpointerdown={e => e.preventDefault()}
				onmousedown={handleClearInput}
			/>
		{/if}
	</div>
</div>
{#if $open}
	<!-- eslint-disable-next-line svelte/require-store-reactive-access -->
	<div use:overlay {...$overlay} class="fixed inset-0 z-10"></div>
	<!-- eslint-disable-next-line svelte/require-store-reactive-access -->
	<div transition:fade={{ duration: 100 }} use:content {...$content} class="lt-sm:fixed! lt-sm:top-50%! lt-sm:left-50%! lt-sm:translate-[-50%,-50%,0]">
		<Calendar
			calendarConfig={{
				maxValue: activeDay && activeDay.add({ years: 1 }),
				minValue: activeDay && startOfMonth(activeDay),
				locale: languageTag(),
				fixedWeeks: true,
				isDateDisabled,
				value,
			}}
			bind:dates={datesRange}
			bind:this={childRef}
			on:cancelCalendarClick={handleCancelation}
			on:validateCalendarClick={handleValidation}
		/>
	</div>
{/if}

<style>
  [data-melt-popover-content] {
    --uno: z-10 min-w-auto left-0 rounded-lg bg-transparent shadow-sm;
  }

  [data-melt-popover-trigger] {
    --uno:  transition-all;
  }

	[data-melt-popover-overlay] {
		background-color: rgba(29, 49, 64, 0.5);
		backdrop-filter: blur(4px);

		@media screen and (min-width: 960px) {
				backdrop-filter: unset;
		}
	}
</style>
